import { fg } from '@confluence/feature-gating';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { COMMENTS_PUBSUB_EVENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';

/** Which handler produced this experience event. Some inline events are handled by both editor and renderer */
export enum CommentsPubSubSource {
	EditorInlineCommentsPubSub = 'editorInlineCommentsPubSub',
	RendererInlineCommentsPubSub = 'rendererInlineCommentsPubSub',
	PageCommentsPubSub = 'pageCommentsPubSub',
}

export type CommentsPubSubExperienceTracker = {
	start: (event: string) => void;
	succeed: () => void;
	fail: (err: unknown) => void;
};

export const createCommentsPubSubExperienceTracker = (
	source: CommentsPubSubSource,
): CommentsPubSubExperienceTracker => {
	// Unique per event invocation; lets us detect concurrent-event collisions
	// by matching initialTrackingId === terminalTrackingId in logs.
	let trackingId: string | undefined;

	return {
		start: (event) => {
			if (!fg('cc_comments_pubsub_experience_tracking_enabled')) return;
			// IMPORTANT - because this is in a factory method, it is deliberate that mixing
			// & matching starts / failures / successes across invocations will lead to different trackingIDs.
			// If you need to refactor this to be cross-file, or cross-function for a valid case,
			// you'll probably need to lift this out of the factory
			trackingId = crypto.randomUUID();
			getExperienceTracker().start({
				name: COMMENTS_PUBSUB_EVENT_EXPERIENCE,
				// 15s — above the AGG client's ~10s network timeout.
				timeout: 15000,
				attributes: {
					event,
					source,
					initialTrackingId: trackingId,
					...getCommentsDataManagerExperimentAttributes(),
				},
			});
		},
		succeed: () => {
			if (!fg('cc_comments_pubsub_experience_tracking_enabled')) return;
			getExperienceTracker().succeed({
				name: COMMENTS_PUBSUB_EVENT_EXPERIENCE,
				attributes: { terminalTrackingId: trackingId },
			});
		},
		fail: (err) => {
			if (!fg('cc_comments_pubsub_experience_tracking_enabled')) return;
			getExperienceTracker().fail({
				name: COMMENTS_PUBSUB_EVENT_EXPERIENCE,
				error: err instanceof Error ? err : new Error(String(err)),
				attributes: { terminalTrackingId: trackingId },
			});
		},
	};
};
